// Tabs Component - Reusable tab navigation

import { type ReactNode, createContext, useContext, useState, useEffect, useRef } from 'react';
import styles from './Tabs.module.css';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs provider');
  }
  return context;
}

interface TabsProps {
  defaultTab?: string;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultTab, activeTab: controlledActiveTab, onTabChange, children, className = '' }: TabsProps) {
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(defaultTab ?? '');

  // Controlled vs uncontrolled mode
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : uncontrolledActiveTab;

  const setActiveTab = (tab: string) => {
    if (onTabChange) {
      onTabChange(tab);
    }
    if (!isControlled) {
      setUncontrolledActiveTab(tab);
    }
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={`${styles.tabs} ${className}`}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

interface TabListProps {
  children: ReactNode;
  className?: string;
}

export function TabList({ children, className = '' }: TabListProps) {
  return (
    <div className={`${styles.tabList} ${className}`} role="tablist">
      {children}
    </div>
  );
}

interface TabProps {
  value: string;
  children: ReactNode;
  badge?: ReactNode;
}

export function Tab({ value, children, badge }: TabProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      className={`${styles.tab} ${isActive ? styles.active : ''}`}
      onClick={() => setActiveTab(value)}
    >
      <span className={styles.tabText}>{children}</span>
      {badge && <span className={styles.tabBadge}>{badge}</span>}
    </button>
  );
}

interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
  onActivate?: () => void;
}

export function TabPanel({ value, children, className = '', onActivate }: TabPanelProps) {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === value;
  const wasActiveRef = useRef(false);

  // Call onActivate when tab becomes active
  useEffect(() => {
    if (isActive && !wasActiveRef.current && onActivate) {
      onActivate();
    }
    wasActiveRef.current = isActive;
  }, [isActive, onActivate]);

  if (!isActive) {
    return null;
  }

  return (
    <div role="tabpanel" className={`${styles.tabPanel} ${className}`}>
      {children}
    </div>
  );
}
